import React, { useState } from 'react'
import { IconType } from 'react-icons'
import { Icon } from '@chakra-ui/icons'
import { Box, Button, Text } from '@chakra-ui/react'
import { Checkbox, CheckboxGroup } from '@chakra-ui/react'
import {
    FaCalendar,
    FaCalendarCheck,
    FaChevronLeft,
    FaChevronRight,
    FaLink,
    FaStar,
} from 'react-icons/fa6'
import { BiDetail } from 'react-icons/bi'

import {
    AppointmentFieldsFragment,
    ClientFieldsFragment,
    ClinicFieldsFragment,
    ClinicSettingsFieldsFragment,
} from '~graphql/generated/graphql'
import FormsList from './list'
import AppointmentSelect from '../appointment-select'
import SharedModal from '~components/shared/modals/shared-modal'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedHelpText from '~components/shared/form/shared-help-text'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { useStateWithReset } from '~utils/hooks/use-state-with-reset'
import { replaceTemplateVariables } from '~utils/replace-template-variables'
import SharedSelectableOption from '~components/shared/wrappers/shared-selectable-option'

/**
 * Confirmation link uses the `appointment_id` merge tag by default. In the
 * inbox we swap it for the selected appointment's real id; in notification
 * templates the tag is left intact so it resolves per-recipient at send time.
 */
const APPOINTMENT_CONFIRMATION_LINK =
    'https://portal.chckup.app/confirm/{{ appointment_id }}'
const APPOINTMENT_ID_TAG_REGEX = /{{\s*appointment_id\s*}}/
const FORM_APPOINTMENT_QUERY_PARAM = 'appointment'

enum SelectedLinkType {
    QuickLink = 'quickLink',
    Form = 'form',
}

type ModalPage = 'main' | 'forms'

interface Props {
    isOpen: boolean
    onClose: () => void
    clinic:
        | (Pick<ClinicFieldsFragment, 'id' | 'slug'> & {
              settings?:
                  | Pick<ClinicSettingsFieldsFragment, 'portal_links'>
                  | null
                  | undefined
          })
        | null
        | undefined
    text: string
    setText: (text: string) => void
    shouldReplaceText?: boolean
    /**
     * When provided (inbox context), appointment-aware links can be tied to a
     * real appointment. Omit it (notification templates) to keep merge fields
     * unresolved until send time.
     */
    client?: Pick<ClientFieldsFragment, 'id'> | null
    /** Appointment to pre-select, e.g. the one the chat was opened from. */
    appointment?: AppointmentFieldsFragment
}

export default function SendFormModal({
    isOpen,
    onClose,
    clinic,
    text,
    setText,
    shouldReplaceText = false,
    client,
    appointment,
}: Props) {
    const { onCompleted, onError } = useToastFeedback(
        'Link selected.',
        'You must select a link.',
    )

    const baseReviewUrl = `https://${process.env.NODE_ENV === 'development' ? 'dev.' : ''}portal.chckup.app/review/${clinic?.slug}`
    const [reviewUrl, setReviewUrl, resetReviewUrl] =
        useStateWithReset<string>(baseReviewUrl)

    const [selectedLink, setSelectedLink, resetLink] = useStateWithReset<
        string | undefined
    >(undefined)
    const [selectedLinkType, setSelectedLinkType, resetSelectedLinkType] =
        useStateWithReset<SelectedLinkType | undefined>(undefined)

    const [selectedAppointment, setSelectedAppointment] = useState<
        AppointmentFieldsFragment | undefined
    >(appointment)
    const [page, setPage] = useState<ModalPage>('main')

    const isInboxContext = !!client
    const isSelectedFormLink = selectedLinkType === SelectedLinkType.Form
    const isSelectedReviewLink =
        selectedLinkType === SelectedLinkType.QuickLink &&
        selectedLink === reviewUrl
    const linkHasAppointmentTag =
        !!selectedLink && APPOINTMENT_ID_TAG_REGEX.test(selectedLink)
    // Main page: the appointment picker follows confirmation-style tagged
    // links and the review link. Form links get their picker on the form
    // subpage instead, always rendered once a form flow is open.
    const shouldShowAppointmentSelect =
        isInboxContext && (linkHasAppointmentTag || isSelectedReviewLink)

    function handleSetReviewUrl(url: string) {
        setReviewUrl(url)
        setSelectedLink(url)
        setSelectedLinkType(SelectedLinkType.QuickLink)
    }

    function handleSelectQuickLink(link: string) {
        setSelectedLink(link)
        setSelectedLinkType(SelectedLinkType.QuickLink)
    }

    function handleSelectFormLink(link: string | undefined) {
        setSelectedLink(link)
        setSelectedLinkType(link ? SelectedLinkType.Form : undefined)
    }

    function handleReset() {
        resetReviewUrl()
        resetLink()
        resetSelectedLinkType()
        setSelectedAppointment(appointment)
        setPage('main')
    }

    // Inbox links can use the selected appointment immediately: form and
    // review links get it as a query param, while tagged links get the merge
    // tag resolved.
    function resolveLink(link: string) {
        if (!isInboxContext) return link

        if (
            (isSelectedFormLink || isSelectedReviewLink) &&
            selectedAppointment?.id
        ) {
            return attachAppointmentIdToUrl(link, selectedAppointment.id)
        }

        const { value } = replaceTemplateVariables(link, {
            appointment_id: selectedAppointment?.id || '',
        })
        return value
    }

    function handleSave() {
        if (!selectedLink) {
            onError()
            return
        }

        const resolvedLink = resolveLink(selectedLink)

        if (shouldReplaceText) {
            setText(resolvedLink)
        } else {
            setText(`${text}${resolvedLink}`)
        }
        onCompleted()
    }

    const quickLinks = [
        {
            label: 'Scheduling link',
            icon: FaCalendar,
            url: `https://portal.chckup.app/book/${clinic?.slug}`,
        },
        {
            label: 'Appointment Confirmation Link',
            icon: FaCalendarCheck,
            url: APPOINTMENT_CONFIRMATION_LINK,
        },
        {
            label: 'Review link',
            icon: FaStar,
            url: reviewUrl,
            configComponent: (
                <ReviewUrlConfig
                    reviewUrl={reviewUrl}
                    setReviewUrl={handleSetReviewUrl}
                />
            ),
        },
        ...(clinic?.settings?.portal_links?.map(p => ({
            label: p.label,
            icon: FaLink,
            url: p.url,
        })) || []),
    ]

    return (
        <SharedModal
            size="2xl"
            isOpen={isOpen}
            onClose={() => {
                handleReset()
                onClose()
            }}
            onCancel={handleReset}
            onConfirm={handleSave}
            confirmText="Select Link"
            title={page === 'forms' ? 'Select a form' : 'Select a link to add'}
            isConfirmDisabled={!selectedLink}
        >
            {page === 'main' ? (
                <>
                    <SharedStack gap={2} mb={6}>
                        {quickLinks.map((l, i) => (
                            <QuickLink
                                key={i}
                                onSelectLink={handleSelectQuickLink}
                                isSelected={selectedLink === l.url}
                                {...l}
                            />
                        ))}
                        <SharedSelectableOption
                            label="Add form link"
                            helpText="Browse forms and attach one to this message"
                            isSelected={isSelectedFormLink}
                            onClick={() => setPage('forms')}
                            leftFlair={
                                <Icon
                                    mr={4}
                                    color="brand.500"
                                    boxSize="16px"
                                    as={BiDetail}
                                />
                            }
                            rightFlair={
                                <Icon
                                    color="gray.400"
                                    boxSize="16px"
                                    as={FaChevronRight}
                                />
                            }
                        />
                    </SharedStack>
                    {shouldShowAppointmentSelect && client && (
                        <SharedStack spacing={1} mb={6}>
                            <AppointmentSelect
                                clinicId={clinic?.id || ''}
                                clientId={client.id}
                                selectedAppointment={selectedAppointment}
                                setSelectedAppointment={setSelectedAppointment}
                            />
                            <SharedHelpText mb={0}>
                                {getAppointmentSelectHelpText(
                                    isSelectedFormLink,
                                    isSelectedReviewLink,
                                )}
                            </SharedHelpText>
                        </SharedStack>
                    )}
                </>
            ) : (
                <>
                    <Button
                        variant="ghost"
                        size="sm"
                        rounded="lg"
                        mb={4}
                        leftIcon={<Icon as={FaChevronLeft} />}
                        onClick={() => setPage('main')}
                    >
                        Back to links
                    </Button>
                    <SharedStack spacing={6} mb={6}>
                        <FormsList
                            selectedLink={selectedLink}
                            onSelectLink={handleSelectFormLink}
                            clinic={clinic}
                            hideHeader
                        />
                    </SharedStack>
                    {isInboxContext && client && (
                        <SharedStack spacing={1} mb={6}>
                            <AppointmentSelect
                                clinicId={clinic?.id || ''}
                                clientId={client.id}
                                selectedAppointment={selectedAppointment}
                                setSelectedAppointment={setSelectedAppointment}
                            />
                            <SharedHelpText mb={0}>
                                {getAppointmentSelectHelpText(true, false)}
                            </SharedHelpText>
                        </SharedStack>
                    )}
                </>
            )}
        </SharedModal>
    )
}

function attachAppointmentIdToUrl(link: string, appointmentId: string): string {
    const url = new URL(link)
    url.searchParams.set(FORM_APPOINTMENT_QUERY_PARAM, appointmentId)
    return url.toString()
}

function getAppointmentSelectHelpText(
    isSelectedFormLink: boolean,
    isSelectedReviewLink: boolean,
): string {
    if (isSelectedFormLink) {
        return 'Choose an appointment to attach to this form link so merge tags can use appointment data. Leave it as "None" to send the form without appointment context.'
    }
    if (isSelectedReviewLink) {
        return 'Choose an appointment to attach to this review link. Leave it as "None" to send the link without appointment context.'
    }
    return 'Choose which appointment this confirmation link should point to. Leave it as "None" to keep the dynamic appointment tag.'
}

function QuickLink({
    label,
    icon,
    url,
    onSelectLink,
    isSelected,
    configComponent,
}: {
    label: string
    icon: IconType
    url: string
    onSelectLink: (link: string) => void
    isSelected: boolean
    configComponent?: React.ReactNode
}) {
    return (
        <SharedSelectableOption
            label={label}
            helpText={url}
            isSelected={isSelected}
            onClick={() => {
                onSelectLink(url)
            }}
            leftFlair={
                <Icon mr={4} color="brand.500" boxSize="16px" as={icon} />
            }
            configComponent={configComponent}
        />
    )
}

function ReviewUrlConfig({
    reviewUrl,
    setReviewUrl,
}: {
    reviewUrl: string
    setReviewUrl: (reviewUrl: string) => void
}) {
    const onlyQueryParam = reviewUrl.split('?')[1]?.split('=')[1]

    return (
        <Box w="full" px={8} pt={2}>
            <Text fontSize="xs" color="gray.600">
                Limit reviews to only:
            </Text>
            <CheckboxGroup
                size="sm"
                colorScheme="brand"
                value={onlyQueryParam ? onlyQueryParam.split('+') : []}
                onChange={val => {
                    if (val.length === 0) {
                        setReviewUrl(reviewUrl.split('?')[0])
                        return
                    }

                    setReviewUrl(
                        reviewUrl.includes('only=')
                            ? reviewUrl.replace(
                                  'only=' + onlyQueryParam,
                                  'only=' + val.join('+'),
                              )
                            : reviewUrl + '?only=' + val.join('+'),
                    )
                }}
            >
                <SharedStack row spacing={4}>
                    <Checkbox value="internal">Chckvet</Checkbox>
                    <Checkbox value="google">Google</Checkbox>
                    <Checkbox value="facebook">Facebook</Checkbox>
                </SharedStack>
            </CheckboxGroup>
        </Box>
    )
}
